export type Channel = 'browser' | 'cli' | 'agent' | 'automation' | 'skill';

export const CHANNELS: { key: Channel; label: string; hint: string }[] = [
  {
    key: 'browser',
    label: 'Web',
    hint: 'Opening the org in a browser from Kruvi Access. This is what a new connection is for.',
  },
  {
    key: 'cli',
    label: 'CLI',
    hint: 'The sf plugin, for retrieving metadata and running scripts from a terminal.',
  },
  {
    key: 'agent',
    label: 'AI agent',
    hint: 'An AI assistant a person has approved in their browser, which mints tokens as them and uses the Salesforce APIs directly.',
  },
  {
    // The honest difference between this and "AI agent" is what the audit log can tell you afterwards, so
    // it is said here, where the choice is actually made, rather than in documentation nobody reads at
    // the moment it would help. The restriction is the lesser reason to pick it.
    key: 'skill',
    label: 'Omni MCP',
    hint:
      'The agent asks Kruvi Access to run the query rather than holding a token itself, and may only do ' +
      'what a skill you have defined allows. Every record and field it touches is recorded; an AI agent ' +
      'session records only that a token was issued. Cannot be combined with AI agent on the same grant.',
  },
  {
    key: 'automation',
    label: 'Automation',
    hint: 'An unattended job with a credential that does not expire until revoked. Best pointed at a Salesforce integration user.',
  },
];

export const channelLabel = (key: Channel): string => CHANNELS.find((c) => c.key === key)?.label ?? key;

interface Props {
  value: Channel[];
  onChange: (next: Channel[]) => void;
  /** Channels the level above allows. Anything outside it is shown disabled with the reason. */
  available?: Channel[];
  disabled?: boolean;
  /** Named so the reason for a disabled row reads correctly wherever this is used. */
  ceilingName?: string;
  /** Which rows to render at all. Use it to leave out a channel the caller could never hold. */
  show?: Channel[];
}

/**
 * The same control at both levels, because it is the same decision twice: a company ceiling, and what one
 * connection is actually for. Web is deliberately not special-cased as un-turn-off-able — an org that is
 * only ever reached by an automation is a reasonable thing to have.
 */
export function ChannelToggles({ value, onChange, available, disabled, ceilingName, show }: Props) {
  const toggle = (key: Channel) => {
    onChange(value.includes(key) ? value.filter((c) => c !== key) : [...value, key]);
  };

  return (
    <div className="channel-list">
      {CHANNELS.filter((c) => !show || show.includes(c.key)).map((c) => {
        const blocked = available !== undefined && !available.includes(c.key);
        return (
          <label key={c.key} className={`channel${blocked ? ' channel-blocked' : ''}`}>
            <input
              type="checkbox"
              checked={value.includes(c.key) && !blocked}
              disabled={disabled || blocked}
              onChange={() => toggle(c.key)}
            />
            <span>
              <strong>{c.label}</strong>
              <span className="small muted"> — {c.hint}</span>
              {blocked && (
                <span className="small muted">
                  {' '}
                  Not available: {ceilingName ?? 'your workspace'} does not allow it.
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}
